import { InfoBox, SocialBox } from '@style/emotion';
import { Link, useParams } from 'react-router-dom';
import { PaymentGatewayName } from '@share/enums';
import { PhaseDetail } from '@share/interfaces';
import { getImgProxySrc } from '@util/helper';
import { payment } from '@state/private';
import { useAPI, useHelper } from 'src/hooks';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { user } from '@state/user';
import { web3 } from '@state/web3';
import CheckoutPaymentModal from '@com/payment/CheckoutPaymentModal';
import Footer from '@com/footer/Footer';
import Header from '@com/header/Header';
import LaunchpadPhraseCard from '@com/card/LaunchpadPhraseCard';
import LoadingContainer from '@com/utils/LoadingContainer';
import ProgressBar from '@com/utils/ProgressBar';
import ShareBtn from '@com/buttons/ShareBtn';
import SkeletonImage from '@com/utils/SkeletonImage';
import TopSection from '@com/TopSection';
import styled from '@emotion/styled';

const LogoContainer = styled.div`
  width: min(300px, 100%);
`;

function LaunchpadDetail(): JSX.Element {
  /* Define state / hook */
  const { t } = useTranslation();
  const { launchpadIdentifier, _paramsChainId } = useParams();
  const paramsChainId = Number(_paramsChainId);
  const { getLaunchpadDetails } = useAPI();
  const { data, isSuccess, isError } = useQuery(
    [getLaunchpadDetails.name, launchpadIdentifier, paramsChainId],
    () => getLaunchpadDetails(String(launchpadIdentifier), paramsChainId),
    { enabled: launchpadIdentifier != null && paramsChainId != null, retry: 0 }
  );
  const { isLogin } = user.useContainer();
  const { getChainName } = web3.useContainer();
  const { primaryColor } = useHelper();
  const { displayName, showDescription, showAddressLink } = useHelper();

  /* Custom Data Object for render or other usages */
  const collection = data?.collection;
  const phrases = data?.phase;

  /* Render control function / logic */
  const now = new Date().getTime() / 1000;

  // Live phase first, then upcoming phase, finally is end phase
  const userFriendlyOrder = (a: PhaseDetail, b: PhaseDetail) => {
    const isAWithinNow = a.start_at <= now && a.end_at >= now;
    const isBWithinNow = b.start_at <= now && b.end_at >= now;

    if (isAWithinNow && !isBWithinNow) {
      return -1;
    }
    if (!isAWithinNow && isBWithinNow) {
      return 1;
    }
    if (isAWithinNow && isBWithinNow) {
      return a.start_at - b.start_at;
    }
    const isABeforeNow = a.end_at < now;
    const isBBeforeNow = b.end_at < now;

    if (isABeforeNow && !isBBeforeNow) {
      return 1;
    }
    if (!isABeforeNow && isBBeforeNow) {
      return -1;
    }
    if (isABeforeNow && isBBeforeNow) {
      return b.end_at - a.end_at;
    }
    return a.start_at - b.start_at;
  };

  /* Data Change Listeners - useEffect/useMemo/useCallback */

  /* Styling */
  const bannerImg = collection?.banner_image;
  const Section = useMemo(
    () => styled.section`
      background-image: url(${getImgProxySrc(bannerImg, 1920)});
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
      background-color: var(--bg-section2);
    `,
    [bannerImg]
  );

  const Main = useMemo(
    () => styled.div`
      margin-top: 80px;
      backdrop-filter: blur(15px);
      background: ${`${primaryColor}cc`};
    `,
    [primaryColor]
  );

  return (
    <payment.Provider>
      <Header />
      <TopSection />
      <Section className="content-container">
        <LoadingContainer isError={isError} isSuccess={isSuccess}>
          <Main className="sc-card-activity style1 row mx-3 d-flex">
            <div className="col-lg-4 col-12 mb-5 align-self-start">
              <div className="card-media-h6 w-100">
                <div className="d-flex justify-content-center mt-5">
                  <LogoContainer className="mb-2">
                    <SkeletonImage
                      alt="Collection Logo"
                      ratio={1}
                      size={[512]}
                      src={collection?.logo_image}
                    />
                  </LogoContainer>
                </div>
                <ProgressBar
                  minted={Number(collection?.total_nft)}
                  total={Number(data?.nft_quantity)}
                />
              </div>
            </div>
            <div className="d-flex col-lg-8 col-12 flex-column">
              <div className="row">
                <h1 className="d-flex col-12 align-items-center fa-3x">
                  <span className="mr-3">{collection?.name}</span>
                  <ShareBtn className="ml-auto ml-5 p-4" />
                </h1>
              </div>
              <div className="row mt-5 ml-2 mr-auto">
                <div className="widget-social style-3">
                  <SocialBox>
                    {data &&
                      Object.values(data?.social_links)?.map((item) => {
                        return (
                          item?.url && (
                            <li key={item?.name}>
                              <a
                                href={item?.url}
                                rel="noreferrer"
                                target="_blank"
                              >
                                <i className={item?.icon} />
                              </a>
                            </li>
                          )
                        );
                      })}
                  </SocialBox>
                </div>
              </div>

              <div className="mt-5">
                <InfoBox>
                  {showDescription(collection?.description) ||
                    t('general.noDescription')}
                </InfoBox>
                <InfoBox>
                  <span className="mr-3">{t('itemDetails.network')}</span>
                  {getChainName(collection?.network_chain_id)}
                </InfoBox>
                <InfoBox>
                  <span className="mr-3">{t('launchpad.creatorName')}</span>
                  <Link to={`/profile/${data?.creator_uuid}`}>
                    {displayName(data?.creator_name)}
                  </Link>
                </InfoBox>
                {collection?.name && collection?.address && (
                  <InfoBox>
                    <span className="mr-3">{t('itemDetails.collection')}</span>
                    <Link
                      to={`/collection/${collection?.network_chain_id}/${collection.address}`}
                    >
                      {collection.name}
                    </Link>
                  </InfoBox>
                )}
                {collection?.address && (
                  <InfoBox>
                    <span className="mr-3">
                      {t('itemDetails.contractAddress')}
                    </span>
                    {showAddressLink(collection.address, paramsChainId)}
                  </InfoBox>
                )}
                <InfoBox>
                  <span className="mr-3">{t('itemDetails.totalQuantity')}</span>
                  {data?.nft_quantity}
                </InfoBox>
              </div>
            </div>
          </Main>

          <div className="tf-activity s1 py-5">
            <div className="themesflat-container">
              <div className="row">
                <div className="col-12">
                  {phrases?.sort(userFriendlyOrder)?.map((phrase) => (
                    <LaunchpadPhraseCard
                      key={phrase.uuid}
                      collectionAddress={String(collection?.address)}
                      data={phrase}
                      launchpadId={String(data?.uuid)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </LoadingContainer>
      </Section>
      <Footer />
      {isLogin &&
        phrases
          ?.map((p) => p?.payment_method?.payment_gateway?.name)
          .includes(PaymentGatewayName.CHECKOUT) && <CheckoutPaymentModal />}
    </payment.Provider>
  );
}

export default LaunchpadDetail;
